import type {
  ChatMessage as UiChatMessage,
  ChatSession as UiChatSession,
} from "./chat-service";

// Lightweight REST + WS client for the public Chat API (ChatApiStack)
// Reads configuration from Vite env vars:
// - VITE_PUBLIC_CHAT_API_URL: base URL of the public Chat API
// - VITE_PUBLIC_CHAT_API_KEY: API key for X-API-Key header
// - VITE_PUBLIC_CHAT_WS_URL: WebSocket URL (wss://...)
// - Optional tenant id can come from the caller; we also read VITE_DEFAULT_TENANT_ID as a fallback

type MessageType = "TEXT" | "IMAGE" | "FILE";

type RestChatMessage = {
  messageId: string;
  sessionId: string;
  content: string;
  messageType: MessageType;
  sender: "USER" | "BOT" | "ADMIN";
  timestamp: string;
  metadata?: Record<string, any>;
};

type RestChatSession = {
  sessionId: string;
  tenantId: string;
  conversationId?: string;
  assetId?: string;
  deviceType: "MOBILE" | "DESKTOP" | "TABLET";
  userAgent?: string;
  ipAddress?: string;
  createdAt: string;
  updatedAt: string;
  status: "ACTIVE" | "ENDED";
};

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  timestamp?: string;
};

export class PublicChatApi {
  private baseUrl: string | undefined;
  private apiKey: string | undefined;
  private wsUrl: string | undefined;
  private cachedTenantId: string | undefined;
  private socket: WebSocket | null = null;
  private socketReady: Promise<void> | null = null;

  constructor() {
    const env = (import.meta as any).env as Record<string, string | undefined>;
    this.baseUrl = env.VITE_PUBLIC_CHAT_API_URL || env.VITE_CHAT_API_URL;
    this.apiKey = env.VITE_PUBLIC_CHAT_API_KEY || env.VITE_CHAT_API_KEY;
    this.wsUrl = env.VITE_PUBLIC_CHAT_WS_URL || env.VITE_CHAT_WS_URL;
    this.cachedTenantId = undefined;
  }

  isConfigured(): boolean {
    return !!this.baseUrl && !!this.apiKey;
  }

  private headers(tenantId?: string): HeadersInit {
    const finalTenant = tenantId || this.cachedTenantId;
    const h: HeadersInit = {
      "Content-Type": "application/json",
      "X-API-Key": this.apiKey || "",
    };
    if (finalTenant) (h as any)["X-Tenant-ID"] = finalTenant;
    return h;
  }

  setTenantId(tenantId?: string) {
    this.cachedTenantId = tenantId;
  }

  getTenantId(): string | undefined {
    return this.cachedTenantId;
  }

  async createSession(params: {
    tenantId?: string;
    conversationId?: string;
    assetId?: string;
    trackingCode?: string;
    autoResponderId?: string;
    // Optional: array of destination links to auto-publish as system messages
    destinations?: Array<{ name?: string; url: string }>;
    deviceType?: "PHONE" | "MOBILE" | "DESKTOP" | "TABLET" | "OTHER";
    userAgent?: string;
  }): Promise<UiChatSession> {
    if (!this.baseUrl) throw new Error("Public Chat API URL not configured");

    const body = {
      // Intentionally do not include a default tenant; server will resolve via conversation or tracking
      tenantId: params.tenantId,
      conversationId: params.conversationId,
      assetId: params.assetId,
      trackingCode: params.trackingCode,
      autoResponderId: params.autoResponderId,
      deviceType:
        (params.deviceType === "PHONE" ? "MOBILE" : params.deviceType) ||
        "DESKTOP",
      userAgent:
        params.userAgent ||
        (typeof navigator !== "undefined" ? navigator.userAgent : "unknown"),
    } as any;

    const res = await fetch(`${this.baseUrl}/chat/session`, {
      method: "POST",
      headers: this.headers(params.tenantId),
      body: JSON.stringify(body),
    });

    const payload = (await res.json()) as ApiResponse<RestChatSession>;
    if (!res.ok || !payload.success || !payload.data) {
      throw new Error(
        payload.error || `Failed to create session (status ${res.status})`
      );
    }

    // Cache tenantId from server response for subsequent calls
    this.setTenantId(payload.data.tenantId);

    return this.mapSession(payload.data);
  }

  async sendMessage(
    sessionId: string,
    content: string,
    messageType: MessageType = "TEXT",
    tenantId?: string
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    if (!this.baseUrl) throw new Error("Public Chat API URL not configured");

    const res = await fetch(`${this.baseUrl}/chat/message`, {
      method: "POST",
      headers: this.headers(tenantId),
      body: JSON.stringify({ sessionId, content, messageType }),
    });

    const payload = (await res.json()) as ApiResponse<RestChatMessage>;
    if (!res.ok || !payload.success || !payload.data) {
      return {
        success: false,
        error: payload.error || `Failed (status ${res.status})`,
      };
    }

    return { success: true, messageId: payload.data.messageId };
  }

  async getMessages(
    sessionId: string,
    tenantId?: string
  ): Promise<UiChatMessage[]> {
    if (!this.baseUrl) throw new Error("Public Chat API URL not configured");

    const res = await fetch(
      `${this.baseUrl}/chat/messages/${encodeURIComponent(sessionId)}`,
      {
        method: "GET",
        headers: this.headers(tenantId),
      }
    );

    const payload = (await res.json()) as ApiResponse<RestChatMessage[]>;
    if (!res.ok || !payload.success || !payload.data) {
      if (payload.error) console.warn("getMessages error:", payload.error);
      return [];
    }

    return payload.data.map((m) => this.mapMessage(m));
  }

  async getConversation(
    conversationId: string,
    tenantId?: string
  ): Promise<any | null> {
    if (!this.baseUrl) throw new Error("Public Chat API URL not configured");

    const res = await fetch(
      `${this.baseUrl}/chat/conversation/${encodeURIComponent(conversationId)}`,
      {
        method: "GET",
        headers: this.headers(tenantId),
      }
    );

    const payload = (await res.json()) as ApiResponse<any>;
    if (!res.ok || !payload.success || !payload.data) {
      if (payload.error) console.warn("getConversation error:", payload.error);
      return null;
    }

    return payload.data;
  }

  async getSession(
    sessionId: string,
    tenantId?: string
  ): Promise<UiChatSession | null> {
    if (!this.baseUrl) throw new Error("Public Chat API URL not configured");
    const res = await fetch(
      `${this.baseUrl}/chat/session/${encodeURIComponent(sessionId)}`,
      { method: "GET", headers: this.headers(tenantId) }
    );
    if (!res.ok) return null;
    const payload = (await res.json()) as ApiResponse<RestChatSession>;
    if (!payload.success || !payload.data) return null;
    return this.mapSession(payload.data);
  }

  private mapSession(s: RestChatSession): UiChatSession {
    return {
      sessionId: s.sessionId,
      conversationId: s.conversationId || "",
      tenantId: s.tenantId,
      status: s.status === "ACTIVE" ? "ACTIVE" : "ENDED",
      aiEnabled: false,
      deviceType: s.deviceType,
      startTime: s.createdAt,
      lastActivity: s.updatedAt,
      messageCount: 0,
    };
  }

  private mapMessage(m: RestChatMessage): UiChatMessage {
    const sender =
      m.sender === "USER" ? "user" : m.sender === "BOT" ? "bot" : "system";
    return {
      id: m.messageId,
      content: m.content,
      sender,
      timestamp: new Date(m.timestamp),
      metadata: m.metadata,
    };
  }

  // WS client: connect and subscribe to a session
  async connectAndSubscribe(
    sessionId: string,
    onMessage: (msg: UiChatMessage) => void
  ): Promise<void> {
    if (!this.wsUrl) return; // WS optional
    const tenantId = this.cachedTenantId;

    // If an existing socket is open, reuse it
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log("🔄 Reusing existing WebSocket, sending subscription:", {
        sessionId,
      });
      const subscribeMessage = { action: "subscribe", sessionId };
      console.log(
        "📤 Sending WebSocket subscription (reuse):",
        subscribeMessage
      );
      this.socket.send(JSON.stringify(subscribeMessage));
      return;
    }

    // Normalize WS URL: ensure wss:// scheme and include stage path if missing
    let normalizedWsUrl = this.wsUrl;
    // Convert http[s] -> ws[s]
    normalizedWsUrl = normalizedWsUrl
      .replace(/^http:/i, "ws:")
      .replace(/^https:/i, "wss:");
    try {
      const u = new URL(normalizedWsUrl);
      if (!u.pathname || u.pathname === "/") {
        const env = (import.meta as any).env as Record<
          string,
          string | undefined
        >;
        const wsStage = env.VITE_WEBSOCKET_STAGE || "$default";
        // Always append a stage when none present; default to $default
        u.pathname = `/${wsStage}`;
      }
      normalizedWsUrl = u.toString();
    } catch {
      // best-effort; fall back to original
    }

    if ((import.meta as any).env.VITE_STAGE === "dev") {
      // eslint-disable-next-line no-console
      console.log("Public Chat WS URL (normalized):", normalizedWsUrl);
    }

    this.socket = new WebSocket(
      `${normalizedWsUrl}?x-api-key=${encodeURIComponent(this.apiKey || "")}${
        tenantId ? `&x-tenant-id=${encodeURIComponent(tenantId)}` : ""
      }`
    );

    this.socketReady = new Promise<void>((resolve) => {
      this.socket!.onopen = () => {
        console.log("🔌 WebSocket opened, sending subscription:", {
          sessionId,
        });
        // Subscribe once connected
        const subscribeMessage = { action: "subscribe", sessionId };
        console.log("📤 Sending WebSocket subscription:", subscribeMessage);
        this.socket!.send(JSON.stringify(subscribeMessage));
        resolve();
      };
    });

    this.socket.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        if (payload?.type === "chat.message" && payload?.data) {
          const m = this.mapMessage(payload.data as RestChatMessage);
          onMessage(m);
        }
      } catch (e) {
        // Ignore non-JSON frames
      }
    };

    this.socket.onclose = () => {
      this.socket = null;
      this.socketReady = null;
    };
  }

  // Optional reconnection helper with exponential backoff
  async ensureConnected(
    sessionId: string,
    onMessage: (msg: UiChatMessage) => void
  ) {
    let attempt = 0;
    const maxDelay = 15000;
    while (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      try {
        await this.connectAndSubscribe(sessionId, onMessage);
        return;
      } catch (e) {
        const delay = Math.min(1000 * 2 ** attempt, maxDelay);
        await new Promise((r) => setTimeout(r, delay));
        attempt++;
      }
    }
  }
}
